import numbro from 'numbro';
import React, { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Table from 'react-bootstrap/Table';
import { useTranslation } from 'react-i18next';
import { FaList } from 'react-icons/fa';

import LikeOverlay from '../../../components/like/LikeOverlay';
import ConcernBarChart from '../../../components/visualize/ConcernBarChart';
import { uiHelpers } from '../../../helpers';
import { bsTiny } from '../../../helpers/ui.helper';
import { CCASSSummary } from '../../../py-generated/datalouder/ccass/types';
import TutorialQuestionMark from '../../Help/TutorialQuestionMark';
import CCASSStockHistory from './CCASSStockHistory';

const shareInCCASS = summary =>
  summary.ccass_shares + summary.consent_shares + summary.anonymous_shares;

const shareInCCASSPercentage = summary =>
  (summary.ccass_shares + summary.consent_shares + summary.anonymous_shares) /
  summary.issued_shares;

const shareNotInCCASS = summary =>
  summary.issued_shares -
  (summary.ccass_shares + summary.consent_shares + summary.anonymous_shares);

const percentageNotInCCASS = summary =>
  1 -
  (summary.ccass_shares + summary.consent_shares + summary.anonymous_shares) /
    summary.issued_shares;

const SHAREHOLDER_IN_CCASS = 'Shareholding in CCASS';
const PER_IN_CCASS = '% of the total number of Issued Shares/ Warrants/ Units';
const MARKET_INTER = 'Market Intermediaries';
const CONSENT_PAR = 'Consenting Investor Participants';
const NON_CONSENT_PAR = 'Non-consenting Investor Participants';
const TOTAL_CCASS = 'Total CCASS';
export const NOT_IN_CCASS = 'Securities not in CCASS';
const TOTAL_ISSUES = 'Total number of Issued Shares/Warrants/Units';
const TOP_N_CON = 'Top N Concentration';

interface Props {
  queryData: CCASSSummary;
  compareData?: CCASSSummary;
}

type HandleClickHistory = (
  index: number,
  concentration?: boolean
) => () => void;

const SummaryTable: React.FC<
  Props & { onClickHistory: HandleClickHistory }
> = ({ queryData, compareData, onClickHistory }) => {
  const { t } = useTranslation();

  const summaryCompareSpan = (accessor, format, showPercent = true) => {
    if (typeof accessor === 'string') {
      const accessProp = accessor;
      accessor = item => item[accessProp];
    }

    return (
      <span className="compared-to-span">
        {' '}
        (
        {accessor(queryData) - accessor(compareData) === 0 ? (
          '='
        ) : (
          <span>
            <span
              style={{
                color: format.includes('%')
                  ? uiHelpers.colorForChange(
                      accessor(queryData) - accessor(compareData)
                    )
                  : 'default',
              }}
            >
              {numbro(accessor(queryData) - accessor(compareData)).format(
                '+' + format
              )}
            </span>
            {showPercent && (
              <span>
                {' | '}
                <i
                  style={{
                    color: uiHelpers.colorForChange(
                      (accessor(queryData) - accessor(compareData)) /
                        accessor(compareData)
                    ),
                  }}
                >
                  {numbro(
                    (accessor(queryData) - accessor(compareData)) /
                      accessor(compareData)
                  ).format('+0.0%')}
                </i>
              </span>
            )}
          </span>
        )}
        )
      </span>
    );
  };

  const summaryCell = (accessor, format, showPercent = true) => {
    if (typeof accessor === 'string') {
      const accessProp = accessor;
      accessor = item => item[accessProp];
    }

    return (
      <span>
        <span>{numbro(accessor(queryData)).format(format)}</span>
        {compareData && summaryCompareSpan(accessor, format, showPercent)}
      </span>
    );
  };

  const summaryCellForParticipantNo = accessor => {
    if (typeof accessor === 'string') {
      const accessProp = accessor;
      accessor = item => item[accessProp];
    }

    return (
      <span>
        <span>
          {compareData && (
            <>
              {numbro(
                (accessor(queryData) - accessor(compareData)) /
                  accessor(compareData)
              ).format('+0.00%')}{' '}
              ({numbro(accessor(compareData)).format('0,0')} →{' '}
            </>
          )}
          {numbro(accessor(queryData)).format('0,0')})
        </span>
      </span>
    );
  };

  return (
    <Table bordered size="sm" className="ccass-summary-table">
      <tbody>
        <tr>
          <th></th>
          <th>{t(SHAREHOLDER_IN_CCASS)}</th>
          <th>{t(PER_IN_CCASS)}</th>
          <th>
            {t('Number of Participants')}
            <TutorialQuestionMark tutorialId={28} alwaysVisible />
          </th>
        </tr>
        <tr>
          <th>
            {t(MARKET_INTER)}
            <Button variant="link" size={bsTiny} onClick={onClickHistory(0)}>
              <FaList />
            </Button>
            <TutorialQuestionMark tutorialId={25} alwaysVisible />
          </th>
          <td>{summaryCell('ccass_shares', '0,0')}</td>
          <td>
            {summaryCell(
              summary => summary.ccass_shares / summary.issued_shares,
              '0.00%',
              false
            )}
          </td>
          <td>{summaryCellForParticipantNo('participants_no')}</td>
        </tr>
        <tr>
          <th>
            {t(CONSENT_PAR)}
            <Button variant="link" size={bsTiny} onClick={onClickHistory(1)}>
              <FaList />
            </Button>
          </th>
          <td>{summaryCell('consent_shares', '0,0')} </td>
          <td>
            {summaryCell(
              summary => summary.consent_shares / summary.issued_shares,
              '0.00%',
              false
            )}
          </td>
          <td>{summaryCellForParticipantNo('consent_no')} </td>
        </tr>
        <tr>
          <th>
            {t(NON_CONSENT_PAR)}
            <Button variant="link" size={bsTiny} onClick={onClickHistory(2)}>
              <FaList />
            </Button>
          </th>
          <td>{summaryCell('anonymous_shares', '0,0')} </td>
          <td>
            {summaryCell(
              summary => summary.anonymous_shares / summary.issued_shares,
              '0.00%',
              false
            )}
          </td>
          <td>{summaryCellForParticipantNo('anonymous_no')} </td>
        </tr>
        <tr>
          <th>
            {t(TOTAL_CCASS)}
            <Button variant="link" size={bsTiny} onClick={onClickHistory(3)}>
              <FaList />
            </Button>
          </th>
          <td>{summaryCell(shareInCCASS, '0,0')} </td>
          <td>{summaryCell(shareInCCASSPercentage, '0.00%', false)}</td>
          <td>
            {summaryCellForParticipantNo(
              summary =>
                summary.participants_no +
                summary.consent_no +
                summary.anonymous_no
            )}{' '}
          </td>
        </tr>
        <tr>
          <th>
            {t(NOT_IN_CCASS)}
            <Button variant="link" size={bsTiny} onClick={onClickHistory(4)}>
              <FaList />
            </Button>
          </th>
          <td>{summaryCell(shareNotInCCASS, '0,0')}</td>
          <td>{summaryCell(percentageNotInCCASS, '0.00%', false)}</td>
          <td></td>
        </tr>
        <tr>
          <th>
            {t(TOTAL_ISSUES)}
            <Button variant="link" size={bsTiny} onClick={onClickHistory(5)}>
              <FaList />
            </Button>
          </th>
          <td>
            {t('(last updated figure)')} {summaryCell('issued_shares', '0,0')}
          </td>
          {/* TODO: bring marketcap back */}
          {/* <th>{t('Mktcap')}</th>
            <td>
              {uiHelpers.formatLargeMoney(
                i18n,
                summary.issued_shares * summary.close
              )}
            </td> */}
        </tr>
      </tbody>
    </Table>
  );
};

const ConcentrationTable: React.FC<
  Props & { onClickHistory: HandleClickHistory }
> = ({ queryData, compareData, onClickHistory }) => {
  const { t } = useTranslation();

  return (
    <Table bordered size="sm" className="ccass-summary-table">
      <tbody>
        <tr>
          <th>N</th>
          <th>{t(TOP_N_CON)}</th>
          <th>{t('Top N Concentration Change')}</th>
          <th>
            {t('Weighted Top N Concentration Change')}
            <TutorialQuestionMark tutorialId={29} alwaysVisible />
          </th>
        </tr>
        {[3, 5, 10, 15].map(n => (
          <tr key={n}>
            <td>{n}</td>
            <td className="inline-canvas-cell">
              <ConcernBarChart
                concern={queryData.concentrations[n.toString()]}
              />
              <Button
                variant="link"
                size={bsTiny}
                style={{ marginRight: '5px' }}
                onClick={onClickHistory(n, true)}
              >
                <FaList />
              </Button>
            </td>
            <td>
              {compareData && (
                <span>
                  {compareData.concentrations[n.toString()] === null
                    ? '-'
                    : numbro(
                        queryData.concentrations[n.toString()] -
                          compareData.concentrations[n.toString()]
                      ).format('+0.00%')}
                </span>
              )}
            </td>
            <td>
              {compareData && (
                <span>
                  {compareData.concentrations[n.toString()] === null
                    ? '-'
                    : numbro(
                        (queryData.concentrations[n.toString()] -
                          compareData.concentrations[n.toString()]) /
                          (1 - compareData.concentrations[n.toString()])
                      ).format('+0.00%')}
                </span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

const CCASSSummaryTables: React.FC<Props> = ({ queryData, compareData }) => {
  const { t, i18n } = useTranslation();

  const [modal, setModal] = useState({
    hShowModal: false,
    hStockCode: undefined,
    hFormatters: [],
    hAccessors: [],
    hLabels: [],
    hTitle: undefined,
  });

  const handleClickHistory = (index, concentration = false) => {
    const typical3Formatters = [
      {
        thousandSeparated: true,
        mantissa: 0,
      },
      '0.00%',
      {
        thousandSeparated: true,
        mantissa: 0,
      },
    ];
    const typical3Labels = [
      t(SHAREHOLDER_IN_CCASS),
      t(PER_IN_CCASS),
      t('Number of Participants'),
    ];
    const metas = [
      {
        formatters: typical3Formatters,
        accessors: [
          'ccass_shares',
          summary => summary.ccass_shares / summary.issued_shares,
          'participants_no',
        ],
        labels: typical3Labels,
        title: t(MARKET_INTER),
      },
      {
        formatters: typical3Formatters,
        accessors: [
          'consent_shares',
          summary => summary.consent_shares / summary.issued_shares,
          'consent_no',
        ],
        labels: typical3Labels,
        title: t(CONSENT_PAR),
      },
      {
        formatters: typical3Formatters,
        accessors: [
          'anonymous_shares',
          summary => summary.anonymous_shares / summary.issued_shares,
          'anonymous_no',
        ],
        labels: typical3Labels,
        title: t(NON_CONSENT_PAR),
      },
      {
        formatters: typical3Formatters,
        accessors: [
          shareInCCASS,
          shareInCCASSPercentage,
          summary =>
            summary.participants_no + summary.consent_no + summary.anonymous_no,
        ],
        labels: typical3Labels,
        title: t(TOTAL_CCASS),
      },
      {
        formatters: typical3Formatters.slice(0, 2),
        accessors: [shareNotInCCASS, percentageNotInCCASS],
        labels: typical3Labels.slice(0, 2),
        title: t(NOT_IN_CCASS),
      },
      {
        formatters: [
          typical3Formatters[0],
          i => uiHelpers.formatLargeMoney(i18n, i),
        ],
        accessors: ['issued_shares'],
        labels: [t(TOTAL_ISSUES)],
        title: t(TOTAL_ISSUES),
      },
    ];

    const concentrationMetas = n => {
      return {
        formatters: ['0.00000%', '+0.00000%', '+0.00000%'],
        accessors: [
          summary =>
            !summary.previous ||
            !summary.previous.concentrations ||
            summary.previous.concentrations[n.toString()] === null
              ? Number.NaN
              : summary.concentrations[n.toString()],
          summary =>
            !summary.previous ||
            !summary.previous.concentrations ||
            summary.previous.concentrations[n.toString()] === null
              ? Number.NaN
              : summary.concentrations[n.toString()] -
                summary.previous.concentrations[n.toString()],
          summary =>
            !summary.previous ||
            !summary.previous.concentrations ||
            summary.previous.concentrations[n.toString()] === null
              ? Number.NaN
              : (summary.concentrations[n.toString()] -
                  summary.previous.concentrations[n.toString()]) /
                (1 - summary.previous.concentrations[n.toString()]),
        ],
        labels: [
          t(TOP_N_CON),
          t('Top N Concentration Change'),
          t('Weighted Top N Concentration Change'),
        ],
        title: t(TOP_N_CON).replace('N', n),
      };
    };

    const { formatters, accessors, labels, title } = concentration
      ? concentrationMetas(index)
      : metas[index];

    return () => {
      setModal({
        hShowModal: true,
        hStockCode: queryData.ticker,
        hFormatters: formatters,
        hAccessors: accessors,
        hLabels: labels,
        hTitle: title,
      });
    };
  };

  return (
    <>
      <h6>
        <LikeOverlay stockCode={queryData.ticker}>
          <span>
            {queryData[`name_${i18n.language}`]}
            {` (${queryData.ticker})`}
          </span>
        </LikeOverlay>
        {' - '}
        {uiHelpers.formatDateLocal(queryData.date)}
        {compareData && (
          <span>
            {` (${t('compared to')} ${uiHelpers.formatDateLocal(
              compareData.date
            )})`}
          </span>
        )}
      </h6>
      <div style={{ width: '100%', overflowX: 'auto' }}>
        <SummaryTable
          queryData={queryData}
          compareData={compareData}
          onClickHistory={handleClickHistory}
        />
      </div>
      <br />
      <ConcentrationTable
        queryData={queryData}
        compareData={compareData}
        onClickHistory={handleClickHistory}
      />
      {modal.hShowModal && (
        <CCASSStockHistory
          stockCode={modal.hStockCode}
          accessors={modal.hAccessors}
          labels={modal.hLabels}
          title={modal.hTitle}
          formatters={modal.hFormatters}
          onHide={() => setModal(_modal => ({ ..._modal, hShowModal: false }))}
        />
      )}
    </>
  );
};

export default CCASSSummaryTables;
